import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Home, Building2, 
  LandPlot, MapPin, Euro, Maximize, TrendingUp, X, Save,
  Image as ImageIcon, Upload, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProperties = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader, loading: authLoading } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    property_type: 'Appartement',
    price: '',
    location: '',
    surface: '',
    rooms: '',
    roi: '',
    estimated_rent: '',
    colocation_active: false,
    price_per_room: '',
    room_count: '',
    market_price_per_sqm: '',
    airbnb_monthly_est: '',
    default_works: '',
    dpe_class: '',
    dpe_conso: '',
    dpe_emissions: '',
    dpe_date: '',
    dpe_note: '',
    dtg_note: '',
    audit_note: '',
    description: '',
    features: '',
    images: '',
    status: 'Disponible',
    published: true
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchProperties();
  }, [isAuthenticated, authLoading, navigate]);

  // Auto-calculate ROI from rent (€/month) and price (€)
  // ROI brut = (loyer mensuel × 12 / prix) × 100
  useEffect(() => {
    const price = parseFloat(formData.price);
    const rent = parseFloat(formData.estimated_rent);
    if (price > 0 && rent > 0) {
      const roi = ((rent * 12) / price) * 100;
      const rounded = Math.round(roi * 10) / 10; // 1 decimal
      setFormData(prev => (prev.roi === rounded.toString() ? prev : { ...prev, roi: rounded.toString() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.price, formData.estimated_rent]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties?published_only=false`, {
        headers: getAuthHeader()
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const propertyData = {
      ...formData,
      price: parseInt(formData.price) || 0,
      surface: parseInt(formData.surface) || 0,
      rooms: formData.rooms ? parseInt(formData.rooms) : null,
      roi: formData.roi ? parseFloat(formData.roi) : null,
      estimated_rent: formData.estimated_rent ? parseInt(formData.estimated_rent) : null,
      price_per_room: formData.price_per_room ? parseInt(formData.price_per_room) : null,
      room_count: formData.room_count ? parseInt(formData.room_count) : null,
      market_price_per_sqm: formData.market_price_per_sqm ? parseInt(formData.market_price_per_sqm) : null,
      airbnb_monthly_est: formData.airbnb_monthly_est ? parseInt(formData.airbnb_monthly_est) : null,
      default_works: formData.default_works ? parseInt(formData.default_works) : null,
      dpe_class: formData.dpe_class || null,
      dpe_conso: formData.dpe_conso ? parseInt(formData.dpe_conso) : null,
      dpe_emissions: formData.dpe_emissions ? parseInt(formData.dpe_emissions) : null,
      dpe_date: formData.dpe_date || null,
      dpe_note: formData.dpe_note || null,
      dtg_note: formData.dtg_note || null,
      audit_note: formData.audit_note || null,
      features: formData.features ? formData.features.split(',').map(f => f.trim()).filter(f => f) : [],
      images: formData.images ? formData.images.split(',').map(i => i.trim()).filter(i => i) : []
    };

    try {
      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, propertyData, {
          headers: getAuthHeader()
        });
      } else {
        await axios.post(`${API}/properties`, propertyData, {
          headers: getAuthHeader()
        });
      }
      setShowForm(false);
      setEditingProperty(null);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      property_type: property.property_type,
      price: property.price.toString(),
      location: property.location,
      surface: property.surface.toString(),
      rooms: property.rooms ? property.rooms.toString() : '',
      roi: property.roi ? property.roi.toString() : '',
      estimated_rent: property.estimated_rent ? property.estimated_rent.toString() : '',
      colocation_active: property.colocation_active || false,
      price_per_room: property.price_per_room ? property.price_per_room.toString() : '',
      room_count: property.room_count ? property.room_count.toString() : '',
      market_price_per_sqm: property.market_price_per_sqm ? property.market_price_per_sqm.toString() : '',
      airbnb_monthly_est: property.airbnb_monthly_est ? property.airbnb_monthly_est.toString() : '',
      default_works: property.default_works ? property.default_works.toString() : '',
      dpe_class: property.dpe_class || '',
      dpe_conso: property.dpe_conso ? property.dpe_conso.toString() : '',
      dpe_emissions: property.dpe_emissions ? property.dpe_emissions.toString() : '',
      dpe_date: property.dpe_date || '',
      dpe_note: property.dpe_note || '',
      dtg_note: property.dtg_note || '',
      audit_note: property.audit_note || '',
      description: property.description,
      features: property.features ? property.features.join(', ') : '',
      images: property.images ? property.images.join(', ') : '',
      status: property.status,
      published: property.published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce bien ?')) return;
    
    try {
      await axios.delete(`${API}/properties/${id}`, {
        headers: getAuthHeader()
      });
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const togglePublished = async (property) => {
    try {
      await axios.put(`${API}/properties/${property.id}`, 
        { published: !property.published },
        { headers: getAuthHeader() }
      );
      fetchProperties();
    } catch (error) {
      console.error('Error toggling published:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        });

        // Construct full URL
        const fullUrl = `${BASE_URL}${response.data.url}`;
        uploadedUrls.push(fullUrl);
      }

      // Add to existing images
      const currentImages = formData.images ? formData.images.split(',').map(i => i.trim()).filter(i => i) : [];
      const allImages = [...currentImages, ...uploadedUrls];
      setFormData({ ...formData, images: allImages.join(', ') });

    } catch (error) {
      console.error('Error uploading:', error);
      alert('Erreur lors de l\'upload. Vérifiez que le fichier est une image (JPG, PNG, WEBP)');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (indexToRemove) => {
    const currentImages = formData.images.split(',').map(i => i.trim()).filter(i => i);
    const newImages = currentImages.filter((_, index) => index !== indexToRemove);
    setFormData({ ...formData, images: newImages.join(', ') });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      property_type: 'Appartement',
      price: '',
      location: '',
      surface: '',
      rooms: '',
      roi: '',
      estimated_rent: '',
      colocation_active: false,
      price_per_room: '',
      room_count: '',
      market_price_per_sqm: '',
      airbnb_monthly_est: '',
      default_works: '',
      dpe_class: '',
      dpe_conso: '',
      dpe_emissions: '',
      dpe_date: '',
      dpe_note: '',
      dtg_note: '',
      audit_note: '',
      description: '',
      features: '',
      images: '',
      status: 'Disponible',
      published: true
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponible': return 'bg-green-100 text-green-700';
      case 'Sous compromis': return 'bg-orange-100 text-orange-700';
      case 'Vendu': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f] mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Biens à la Vente</h1>
          <p className="text-gray-500">{properties.length} bien(s) • Max 10</p>
        </div>
        {properties.length < 10 && (
          <Button 
            onClick={() => { setShowForm(true); setEditingProperty(null); resetForm(); }}
            className="bg-[#1e3a5f] text-white hover:bg-[#2d4a6f]"
          >
            <Plus size={18} className="mr-2" />
            Ajouter un Bien
          </Button>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-[#1e3a5f]">
                {editingProperty ? 'Modifier le Bien' : 'Nouveau Bien'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingProperty(null); }}>
                <X className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ex: Appartement T3 rénové centre-ville"
                  required
                />
              </div>
              
              {/* Type & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option>Appartement</option>
                    <option>Maison</option>
                    <option>Terrain</option>
                    <option>Immeuble</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option>Disponible</option>
                    <option>Sous compromis</option>
                    <option>Vendu</option>
                  </select>
                </div>
              </div>
              
              {/* Price & Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix (€) *</label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="150000"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation *</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Lyon 3ème"
                    required
                  />
                </div>
              </div>
              
              {/* Surface, Rooms, ROI */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surface (m²) *</label>
                  <Input
                    type="number"
                    value={formData.surface}
                    onChange={(e) => setFormData({...formData, surface: e.target.value})}
                    placeholder="65"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pièces</label>
                  <Input
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => setFormData({...formData, rooms: e.target.value})}
                    placeholder="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ROI (%) <span className="text-[#c9a227] text-xs font-normal">· auto</span>
                  </label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.roi}
                    onChange={(e) => setFormData({...formData, roi: e.target.value})}
                    placeholder="Calculé auto"
                    className="bg-amber-50 border-amber-200"
                  />
                </div>
              </div>

              {/* Investment focus: Estimated Rent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loyer mensuel estimé (€)
                </label>
                <Input
                  type="number"
                  value={formData.estimated_rent}
                  onChange={(e) => setFormData({...formData, estimated_rent: e.target.value})}
                  placeholder="850"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Renseignez le loyer + le prix de vente → le <strong>ROI brut</strong> se calcule automatiquement (loyer × 12 / prix). Vous pouvez l'ajuster manuellement.
                </p>
              </div>

              {/* Colocation toggle (only for biens ≥ 80m²) */}
              <div className={`rounded-xl border p-4 ${parseInt(formData.surface) >= 80 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.colocation_active}
                        onChange={(e) => setFormData({...formData, colocation_active: e.target.checked})}
                        disabled={parseInt(formData.surface) < 80}
                        className="w-4 h-4 accent-[#c9a227]"
                      />
                      <span className="text-sm font-semibold text-[#1e3a5f]">Activer le scénario Colocation</span>
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      {parseInt(formData.surface) >= 80
                        ? 'Ajoute un scénario "Colocation par chambre" dans le rapport investisseur.'
                        : 'Disponible uniquement pour les biens de 80 m² ou plus.'}
                    </p>
                  </div>
                </div>
                {formData.colocation_active && parseInt(formData.surface) >= 80 && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Loyer par chambre (€/mois)
                      </label>
                      <Input
                        type="number"
                        value={formData.price_per_room}
                        onChange={(e) => setFormData({...formData, price_per_room: e.target.value})}
                        placeholder="450"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Nombre de chambres
                      </label>
                      <Input
                        type="number"
                        value={formData.room_count}
                        onChange={(e) => setFormData({...formData, room_count: e.target.value})}
                        placeholder="4"
                        className="bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Insights pré-remplis pour le Rapport Investisseur */}
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-700">Données rapport investisseur</span>
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">Optionnel</span>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Pré-remplissez ces valeurs pour qu'elles s'affichent automatiquement dans le rapport investisseur que voient les visiteurs. Si laissé vide, des valeurs par défaut sont calculées.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Prix m² secteur (€/m²)
                    </label>
                    <Input
                      type="number"
                      value={formData.market_price_per_sqm}
                      onChange={(e) => setFormData({...formData, market_price_per_sqm: e.target.value})}
                      placeholder="3500"
                      className="bg-white"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Pour comparaison marché</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Airbnb mensuel (€/mois)
                    </label>
                    <Input
                      type="number"
                      value={formData.airbnb_monthly_est}
                      onChange={(e) => setFormData({...formData, airbnb_monthly_est: e.target.value})}
                      placeholder="1500"
                      className="bg-white"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Estimation locative courte durée</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Travaux estimés (€)
                    </label>
                    <Input
                      type="number"
                      value={formData.default_works}
                      onChange={(e) => setFormData({...formData, default_works: e.target.value})}
                      placeholder="0"
                      className="bg-white"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Enveloppe travaux conseillée</p>
                  </div>
                </div>
              </div>

              {/* Section DIAGNOSTICS (rapport pro) */}
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <h3 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  📋 Diagnostics (pour le rapport pro PDF)
                </h3>
                <div className="grid md:grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Classe DPE</label>
                    <select
                      value={formData.dpe_class}
                      onChange={(e) => setFormData({...formData, dpe_class: e.target.value})}
                      className="w-full bg-white border rounded-md px-3 py-2 text-sm"
                      data-testid="dpe-class-select"
                    >
                      <option value="">—</option>
                      {['A','B','C','D','E','F','G'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Conso (kWh/m²/an)</label>
                    <Input
                      type="number"
                      value={formData.dpe_conso}
                      onChange={(e) => setFormData({...formData, dpe_conso: e.target.value})}
                      placeholder="180"
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Émissions (kg CO₂/m²/an)</label>
                    <Input
                      type="number"
                      value={formData.dpe_emissions}
                      onChange={(e) => setFormData({...formData, dpe_emissions: e.target.value})}
                      placeholder="35"
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date du DPE</label>
                    <Input
                      type="date"
                      value={formData.dpe_date}
                      onChange={(e) => setFormData({...formData, dpe_date: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Note DPE (optionnel)</label>
                    <Textarea
                      value={formData.dpe_note}
                      onChange={(e) => setFormData({...formData, dpe_note: e.target.value})}
                      placeholder="Commentaire libre sur le DPE (ex: en cours de refonte)…"
                      rows={3}
                      className="bg-white text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">DTG — Diag. Technique Global</label>
                    <Textarea
                      value={formData.dtg_note}
                      onChange={(e) => setFormData({...formData, dtg_note: e.target.value})}
                      placeholder="État de la copropriété, travaux votés, provisions…"
                      rows={3}
                      className="bg-white text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Audit énergétique</label>
                    <Textarea
                      value={formData.audit_note}
                      onChange={(e) => setFormData({...formData, audit_note: e.target.value})}
                      placeholder="Résumé de l'audit, préconisations…"
                      rows={3}
                      className="bg-white text-xs"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-purple-700 mt-3 italic">
                  Ces éléments seront intégrés dans le PDF "Rapport Pro" téléchargeable par les visiteurs sur la page du bien.
                </p>
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Décrivez le bien, ses atouts, le contexte de la vente..."
                  rows={4}
                  required
                />
              </div>
              
              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caractéristiques (séparées par des virgules)
                </label>
                <Input
                  value={formData.features}
                  onChange={(e) => setFormData({...formData, features: e.target.value})}
                  placeholder="Parking, Cave, Balcon, Ascenseur"
                />
              </div>
              
              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photos du bien
                </label>
                
                {/* Upload Button */}
                <div className="mb-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full border-dashed border-2 py-8"
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Upload en cours...
                      </>
                    ) : (
                      <>
                        <Upload size={20} className="mr-2" />
                        Cliquer pour ajouter des photos
                      </>
                    )}
                  </Button>
                </div>

                {/* Image Preview */}
                {formData.images && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {formData.images.split(',').map((url, index) => {
                      const trimmedUrl = url.trim();
                      if (!trimmedUrl) return null;
                      return (
                        <div key={index} className="relative group">
                          <img 
                            src={trimmedUrl} 
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Formats acceptés : JPG, PNG, WEBP • Plusieurs images possibles
                </p>
              </div>
              
              {/* Published */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({...formData, published: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="published" className="text-sm text-gray-700">
                  Publier immédiatement
                </label>
              </div>
              
              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#1e3a5f] text-white hover:bg-[#2d4a6f]">
                  <Save size={18} className="mr-2" />
                  {editingProperty ? 'Enregistrer' : 'Créer le Bien'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setShowForm(false); setEditingProperty(null); }}
                >
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Properties List */}
      {properties.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl">
          <Home className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            Aucun bien pour le moment
          </h3>
          <p className="text-gray-500 mb-6">
            Ajoutez votre premier bien à la vente
          </p>
          <Button 
            onClick={() => { setShowForm(true); resetForm(); }}
            className="bg-[#1e3a5f] text-white"
          >
            <Plus size={18} className="mr-2" />
            Ajouter un Bien
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {properties.map((property) => (
            <div 
              key={property.id}
              className={`bg-white rounded-xl border p-4 flex flex-col md:flex-row gap-4 ${
                !property.published ? 'opacity-60' : ''
              }`}
            >
              {/* Image */}
              <div className="w-full md:w-40 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                {property.images && property.images.length > 0 ? (
                  <img 
                    src={property.images[0]} 
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="text-gray-300" size={32} />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusColor(property.status)}`}>
                    {property.status}
                  </span>
                  {property.roi && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#c9a227]/20 text-[#c9a227]">
                      {property.roi}% ROI
                    </span>
                  )}
                  {!property.published && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                      Brouillon
                    </span>
                  )}
                </div>
                
                <h3 className="font-semibold text-[#1e3a5f] mb-1 truncate">{property.title}</h3>
                
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {property.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Maximize size={14} />
                    {property.surface} m²
                  </span>
                  <span className="flex items-center gap-1 font-semibold text-[#1e3a5f]">
                    <Euro size={14} />
                    {formatPrice(property.price)}
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex md:flex-col gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => togglePublished(property)}
                  className="flex-1 md:flex-none"
                >
                  {property.published ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(property)}
                  className="flex-1 md:flex-none"
                >
                  <Edit2 size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(property.id)}
                  className="flex-1 md:flex-none text-red-500 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminProperties;
